import { useCallback, useMemo } from 'react';
import { featureLiveInArea } from '@domain/entities/AreaCatalog';
import { personCan } from '@domain/policies/RbacPolicy';
import { isPermission, type FeatureKey, type Permission } from '@domain/vocabulary';
import type { AreaId } from '@shared/brand';
import { useScopedAreaId } from '../providers/AreaFocusProvider';
import { useRuntimeConfig } from '../providers/RuntimeConfigProvider';
import { useSessionState } from '../providers/SessionProvider';

/**
 * The two questions every screen asks: does this person hold that permission, and is
 * that module switched on.
 *
 * Both answers come from the live configuration, so they change under an open
 * session without a reload. Neither is a security control — the Firestore rules and
 * the privileged callables are — but keeping them in one hook means the UI and the
 * server are reading the same matrix, and a control is never offered that the server
 * would refuse.
 */

export interface Access {
  readonly holds: (permission: Permission) => boolean;
  readonly holdsAny: (permissions: readonly Permission[]) => boolean;
  readonly featureOn: (feature: FeatureKey) => boolean;
}

export const useAccess = (): Access => {
  const config = useRuntimeConfig();
  const { status } = useSessionState();

  const role = status.kind === 'ready' ? status.session.role : null;

  /*
   * Las excepciones de esta persona, no las de su rol.
   *
   * Salen de la ficha que la sesión ya trae, así que no cuestan una consulta. El
   * servidor calcula lo mismo con `permissionsOf` y en el mismo orden —quitar
   * gana—: si discreparan, la pantalla ofrecería botones que la callable rechaza.
   */
  /* En `useMemo` y no sueltas: el `[]` de la rama sin sesión sería un arreglo
     nuevo en cada render, y `holds` se rehace con él — que es lo que convierte un
     hook barato en uno que invalida medio árbol al escribir en un campo. */
  const grants = useMemo(
    () =>
      status.kind === 'ready'
        ? {
            extra: status.person.extraPermissions.filter(isPermission),
            revoked: status.person.revokedPermissions.filter(isPermission),
          }
        : { extra: [], revoked: [] },
    [status],
  );

  const holds = useCallback(
    (permission: Permission): boolean => {
      if (role === null) return false;
      return personCan(config.rolePermissions, role, permission, grants);
    },
    [config.rolePermissions, role, grants],
  );

  const holdsAny = useCallback(
    (permissions: readonly Permission[]): boolean => permissions.some(holds),
    [holds],
  );

  /*
   * El área contra la que se resuelve un módulo: la que se está mirando.
   *
   * Para los cuatro roles que no cambian de área es siempre la suya, así que no
   * cambia nada. Para el superusuario y el rol global es la que tengan en foco, y
   * tiene que serlo: si un módulo lo encendió sólo Tecnología y el superusuario
   * resolviera contra su propia área, no podría ver la cola de aprobaciones que
   * acaba de habilitar.
   */
  const areaId = useScopedAreaId(
    status.kind === 'ready' ? status.session.areaId : ('' as AreaId),
  );
  const areaCatalog = config.areaCatalogs[areaId];

  const featureOn = useCallback(
    (feature: FeatureKey): boolean => featureLiveInArea(config.features, areaCatalog, feature),
    [config.features, areaCatalog],
  );

  return useMemo<Access>(() => ({ holds, holdsAny, featureOn }), [holds, holdsAny, featureOn]);
};
